// The owner report's result blocks: what each ad platform did, where the guests came from, the month's total, the trend,
// "since we started", the guest list, Google, social, loyalty cards and campaign rows. Part of the payload in
// `owner-report-schema.ts`.
import { z } from 'zod';
import { socialReportTopPostSchema } from '../social-report/social-report-schemas';
import { count, isoDate, reportMonthSchema } from './owner-report-schema-primitives';

/**
 * The doors a counted guest came through, in the page's order. One claim that came in counts in one door, so the doors add
 * up to the hero. `META_ADS` is the catch-all: every online claim that names no other door lands there.
 */
export const OWNER_REPORT_DOORS = ['META_ADS', 'COMMENT_DM', 'GOOGLE', 'BIO_PAGE'] as const;
export type OwnerReportDoor = (typeof OWNER_REPORT_DOORS)[number];

/** How many months the trend shows, the report's month included. */
export const OWNER_REPORT_TREND_MONTHS = 6;
/** How far back "since we started" reaches, the report's month included. */
export const OWNER_REPORT_SINCE_START_MONTHS = 36;
/** How many guests the report names. */
export const OWNER_REPORT_RECENT_GUESTS = 5;

/** What the guest list reads for a guest with no usable name. The page may translate it; compare with this, not the word. */
export const OWNER_REPORT_UNNAMED_GUEST = 'Guest';

/** What Meta says the salon's ad account did in the month; null when nothing is stored. */
export const ownerReportMetaAdsSchema = z.object({
  /** Null with no spend, and for staff who may not see money. */
  spendCents: z.number().int().positive().nullable(),
  impressions: count,
  linkClicks: count,
  /** Conversations started + comments on the ads. */
  messages: count,
  /** `ads.gotOffer` minus Google's: every online offer that did not come from Google. */
  gotOffer: count,
});
export type OwnerReportMetaAds = z.infer<typeof ownerReportMetaAdsSchema>;

/** What Google says the salon's ad account did in the month; null when nothing is stored. */
export const ownerReportGoogleAdsSchema = z.object({
  /** Null with no spend, and for staff who may not see money. */
  spendCents: z.number().int().positive().nullable(),
  impressions: count,
  clicks: count,
  calls: count,
  directions: count,
  /** Online offers whose sign-up came from Google. */
  gotOffer: count,
});
export type OwnerReportGoogleAds = z.infer<typeof ownerReportGoogleAdsSchema>;

export const ownerReportDoorsSchema = z.object({ META_ADS: count, COMMENT_DM: count, GOOGLE: count, BIO_PAGE: count });
export type OwnerReportDoors = z.infer<typeof ownerReportDoorsSchema>;

export const ownerReportTotalsSchema = z.object({
  /** Every visit below added up: `adsAndSocial + texted + printed`. */
  visits: count,
  /** The main number, `hero.cameIn`. */
  adsAndSocial: count,
  /** `withoutAds.texted.cameIn`. */
  texted: count,
  /** `withoutAds.printed.cameIn`. */
  printed: count,
});
export type OwnerReportTotals = z.infer<typeof ownerReportTotalsSchema>;

export const ownerReportTrendPointSchema = z.object({ month: reportMonthSchema, cameIn: count });
export type OwnerReportTrendPoint = z.infer<typeof ownerReportTrendPointSchema>;

export const ownerReportSinceStartSchema = z.object({
  /** The first month counted: the first marketing month, or 36 months back at most. */
  fromMonth: reportMonthSchema,
  /** Uses from `fromMonth` through the report's month, under the hero's rule. */
  cameIn: count,
  /** New guests over the span, each person once. */
  newGuests: count,
  /** What those visits were worth, under `ads.value`'s rule; null when that line would be hidden, and for staff who may not see money. */
  valueCents: z.number().int().positive().nullable(),
});
export type OwnerReportSinceStart = z.infer<typeof ownerReportSinceStartSchema>;

export const ownerReportRecentGuestSchema = z.object({
  /** First name + last initial ("Mai T."), or `OWNER_REPORT_UNNAMED_GUEST`. Never a phone or an email. */
  name: z.string(),
  /** New to the salon on that visit, by the hero's rule. */
  isNew: z.boolean(),
  door: z.enum(OWNER_REPORT_DOORS),
  /** The salon-calendar day the guest first came in. */
  on: isoDate,
});
export type OwnerReportRecentGuest = z.infer<typeof ownerReportRecentGuestSchema>;

export const ownerReportRecentGuestsSchema = z.object({
  /** The month's latest guests among the hero's, newest first; 5 at most. */
  rows: z.array(ownerReportRecentGuestSchema),
  /** The hero's guests not listed: `hero.cameIn − rows.length`. */
  more: count,
});
export type OwnerReportRecentGuests = z.infer<typeof ownerReportRecentGuestsSchema>;

/** How many months `google.reviews.byMonth` shows, the report's month last. */
export const OWNER_REPORT_REVIEW_MONTHS = 6;
/** Longest review quote on the report. */
export const OWNER_REPORT_REVIEW_QUOTE_MAX = 160;

/** The month's Google reviews, from the salon's Business Profile. Never a reviewer's name. */
export const ownerReportGoogleReviewsSchema = z.object({
  newCount: count,
  previousCount: count,
  /** Google's rating and review count today: Google keeps no history of them, so an older month reads the same. */
  rating: z.number().nonnegative().nullable(),
  totalCount: count.nullable(),
  /** The month's new reviews that have a reply on Google, from Hayven or not. */
  answered: count,
  byMonth: z.array(z.object({ month: reportMonthSchema, count })),
  /** The month's latest 5★ review with words, cut short; null without one. */
  quote: z.object({ stars: z.number().int().min(1).max(5), text: z.string() }).nullable(),
});
export type OwnerReportGoogleReviews = z.infer<typeof ownerReportGoogleReviewsSchema>;

const googleProfileFigureSchema = z.object({ current: count, previous: count.nullable() });

/** The latest month Google shared, up to the report's month. */
export const ownerReportGoogleProfileSchema = z.object({
  /** Google shares months late, so this can be older than the report's month: the page must print it. */
  month: reportMonthSchema,
  views: googleProfileFigureSchema,
  calls: googleProfileFigureSchema,
  directions: googleProfileFigureSchema,
  websiteClicks: googleProfileFigureSchema,
});
export type OwnerReportGoogleProfile = z.infer<typeof ownerReportGoogleProfileSchema>;

const averageRank = z.number().positive().nullable();

/** Where the salon stands on Google Maps at the latest finished scan, whichever month the report shows. */
export const ownerReportGoogleRankingSchema = z.object({
  keywords: z.array(z.object({ term: z.string(), averageRank, previousAverageRank: averageRank })),
  /** The first keyword's map; null when its scan has no spots stored. Ranks only, never a coordinate. */
  grid: z
    .object({
      /** Spots per side. */
      size: z.number().int().positive(),
      /** `size × size` spots, row by row from the north-west corner; null = not found there, or not checked. */
      ranks: z.array(z.number().int().positive().nullable()),
      /** Positions in `ranks` whose search did not come back: unknown, so never shown as not found. */
      unchecked: z.array(count),
    })
    .nullable(),
});
export type OwnerReportGoogleRanking = z.infer<typeof ownerReportGoogleRankingSchema>;

/** Google's part of the report, null when none of the three has anything to show. */
export const ownerReportGoogleSchema = z.object({
  reviews: ownerReportGoogleReviewsSchema.nullable(),
  profile: ownerReportGoogleProfileSchema.nullable(),
  ranking: ownerReportGoogleRankingSchema.nullable(),
});
export type OwnerReportGoogle = z.infer<typeof ownerReportGoogleSchema>;

/** The month's social numbers, equal to the salon's social report. Captions and stored thumbnails only, never a commenter. */
export const ownerReportSocialSchema = z.object({
  /** New people from Comment → DM and Text Keywords, each person once. */
  newGuests: count,
  cameIn: count,
  comments: count,
  dmsSent: count,
  /** Posts with a comment in the month. */
  posts: count,
  topPosts: z.array(socialReportTopPostSchema),
});
export type OwnerReportSocial = z.infer<typeof ownerReportSocialSchema>;

export const ownerReportLoyaltySchema = z.object({
  /** Cards with a visit stamped or a reward used in the month. */
  active: count,
  /** Cards started in the month. */
  newThisMonth: count,
});
export type OwnerReportLoyalty = z.infer<typeof ownerReportLoyaltySchema>;

export const OWNER_REPORT_CAMPAIGN_KINDS = ['NEW_CLIENT_OFFER', 'VOUCHER', 'GIVEAWAY'] as const;
export type OwnerReportCampaignKind = (typeof OWNER_REPORT_CAMPAIGN_KINDS)[number];

export const ownerReportCampaignRowSchema = z.object({
  kind: z.enum(OWNER_REPORT_CAMPAIGN_KINDS),
  /** Null for the New Client Offer (it is not a campaign). */
  campaignId: z.string().nullable(),
  /** "New Client Offer" or the campaign's name. */
  name: z.string(),
  /** What it gives: "20% off", "$10 off any service ($60+)", "$500 gift card"; null when unknown. */
  detail: z.string().nullable(),
  /** Offers claimed (every placement) / vouchers given / giveaway entries, in the month. */
  got: count,
  /** Offers and vouchers that came in during the month; giveaway entrants who came in during the month. */
  cameIn: count,
});
export type OwnerReportCampaignRow = z.infer<typeof ownerReportCampaignRowSchema>;
